import { useEffect, useMemo, useReducer } from 'react';
import { CronosEthereumTransferType, Network, TokenAsset } from '@types';
import useCalculateCronosEthereumBridgeFee from '@hooks/useCalculateCronosEthereumBridgeFee';
import staticConfig from '@config/static-config';

export interface UseBridgeTransactionFeeInfo {
  amount: string;
  isWaived: boolean;
  tokenAsset: TokenAsset;
}

interface UseBridgeTransactionFeeReducerState {
  feeInfo: UseBridgeTransactionFeeInfo | null;
  hasError: boolean;
  isLoading: boolean;
}

const initState: UseBridgeTransactionFeeReducerState = {
  feeInfo: null,
  hasError: false,
  isLoading: false,
};

const PENDING = 'PENDING';

const FULFILLED = 'FULFILLED';

const REJECTED = 'REJECTED';

const RESET = 'RESET';

interface PendingAction {
  type: typeof PENDING;
}

interface FulfilledAction {
  feeInfo: UseBridgeTransactionFeeInfo;
  type: typeof FULFILLED;
}

interface RejectedAction {
  type: typeof REJECTED;
}

interface ResetAction {
  type: typeof RESET;
}

type UseBridgeTransactionFeeReducerAction =
  | FulfilledAction
  | PendingAction
  | RejectedAction
  | ResetAction;

function reducer(
  state: UseBridgeTransactionFeeReducerState,
  action: UseBridgeTransactionFeeReducerAction
): UseBridgeTransactionFeeReducerState {
  switch (action.type) {
    case PENDING:
      return { ...state, feeInfo: null, hasError: false, isLoading: true };
    case FULFILLED:
      return { ...state, feeInfo: action.feeInfo, isLoading: false };
    case REJECTED:
      return { ...state, hasError: true, isLoading: false };
    case RESET:
      return { ...initState };
    default:
      return { ...state };
  }
}

export interface UseBridgeTransactionFeeParams {
  amount: string;
  cronosEthereumTransferType: CronosEthereumTransferType | null;
  fromNetwork: Network;
  toNetwork: Network;
  tokenAsset: TokenAsset;
}

const CRONOS_POOL_GAS_LIMIT = staticConfig.cronos.gasLimit.pool;

const CRONOS_WARP_GAS_LIMIT = staticConfig.cronos.gasLimit.warp;

type UseBridgeTransactionFeeResult = UseBridgeTransactionFeeReducerState;

function useBridgeTransactionFee(
  params: UseBridgeTransactionFeeParams | null
): UseBridgeTransactionFeeResult {
  const [state, dispatch] = useReducer(reducer, initState);
  const calculateCronosEthereumBridgeFee = useCalculateCronosEthereumBridgeFee({
    cronosPoolGasLimit: CRONOS_POOL_GAS_LIMIT,
    cronosWarpGasLimit: CRONOS_WARP_GAS_LIMIT,
    gasPriceSpeed: 'fastest',
  });

  useEffect(() => {
    let didCancel = false;

    function getPairing(from: Network, to: Network): string {
      return `${from}:${to}`;
    }

    async function getBridgeTransactionFee({
      cronosEthereumTransferType,
      fromNetwork,
      toNetwork,
      tokenAsset,
    }: UseBridgeTransactionFeeParams): Promise<void> {
      dispatch({ type: PENDING });

      switch (getPairing(fromNetwork, toNetwork)) {
        case getPairing(Network.Ethereum, Network.Cronos):
        case getPairing(Network.CryptoOrg, Network.Cronos):
        case getPairing(Network.Cronos, Network.CryptoOrg):
        case getPairing(Network.Cosmos, Network.Cronos):
        case getPairing(Network.Cronos, Network.Cosmos):
        case getPairing(Network.Terra, Network.Cronos):
        case getPairing(Network.Cronos, Network.Terra):
        case getPairing(Network.Juno, Network.Cronos):
        case getPairing(Network.Cronos, Network.Juno):
        case getPairing(Network.Akash, Network.Cronos):
        case getPairing(Network.Cronos, Network.Akash):
        case getPairing(Network.Canto, Network.Cronos):
        case getPairing(Network.Cronos, Network.Canto):
        case getPairing(Network.Iris, Network.Cronos):
        case getPairing(Network.Cronos, Network.Iris):
        case getPairing(Network.Celestia, Network.Cronos):
        case getPairing(Network.Cronos, Network.Celestia):
          if (!didCancel) {
            dispatch({
              type: FULFILLED,
              feeInfo: { amount: '0', isWaived: true, tokenAsset },
            });
          }
          break;
        case getPairing(Network.Cronos, Network.Ethereum):
          const { amount, ok } = await calculateCronosEthereumBridgeFee(
            tokenAsset,
            cronosEthereumTransferType || CronosEthereumTransferType.CronosPool
          );

          if (!didCancel) {
            if (!ok) {
              dispatch({ type: REJECTED });
              break;
            }

            dispatch({
              type: FULFILLED,
              feeInfo: { amount, isWaived: false, tokenAsset },
            });
            break;
          }

          break;
        default:
          dispatch({ type: REJECTED });
      }
    }

    if (params) {
      getBridgeTransactionFee(params);
    } else {
      dispatch({ type: RESET });
    }

    return (): void => {
      didCancel = true;
    };
  }, [calculateCronosEthereumBridgeFee, params]);

  return useMemo(() => state, [state]);
}

export default useBridgeTransactionFee;
